import {
  axiosInstance,
  ColDef,
  Column,
  IFilterDef,
  IServerSideGetRowsParams,
  PaginatedApiResponse,
  ValueGetterParams,
} from '@fides/fides-react-components';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { useMemo } from 'react';
import moment from 'moment';
import isEmpty from 'lodash.isempty';
import { Log } from './initApp';
import { ColumnDef, Constructable, DataModel, Datasource } from '../interfaces';
import { ColumnSettings, GridSettings } from '../models';
import { ClientFileStatus, SignatureStatus, SourceType } from '../enums';
import { FilterMap, JsonObject } from '../types';

export const isColHidden = (colId: ColumnSettings['colId'], displayedCols?: ColumnSettings['colId'][]): boolean =>
  displayedCols && !displayedCols.includes(colId);

export const hasStatusPermission = (permissionsToCheck: SignatureStatus[], statusToCheck: SignatureStatus): boolean =>
  permissionsToCheck.includes(statusToCheck);

export const hasUploadStatusPermission = (
  permissionsToCheck: ClientFileStatus[],
  statusToCheck: ClientFileStatus
): boolean => permissionsToCheck.includes(statusToCheck);

export const hasSourceTypePermission = (permissionsToCheck: SourceType[], sourceTypeToCheck: SourceType): boolean =>
  permissionsToCheck.includes(SourceType[sourceTypeToCheck]);

export const getIbanOrProprietaryIdentification = (param: ValueGetterParams): string => {
  if (param.data && param.data.debitAccount) {
    return param.data.debitAccount.iban
      ? param.data.debitAccount.iban
      : param.data.debitAccount.proprietaryIdentification;
  }
  return null;
};

export const getGridSettings = (
  gridName: GridSettings['gridName'],
  gridsSettings: GridSettings[]
): GridSettings | undefined =>
  gridsSettings ? gridsSettings.filter((settings: GridSettings) => settings.gridName === gridName)[0] : undefined;

export const mapSortModel = (columnMapping = [], sortModel = []) => {
  if (sortModel.length > 0) {
    return sortModel.map(({ sort, colId }) => {
      const col = columnMapping.find((row) => row.colId === colId);
      const sortCol = col?.colDef?.field || colId;
      return `${sortCol},${sort}`;
    });
  }
  return undefined;
};

export const getColIdBackend = (colId: string, colDefs: ColumnDef[]): ColumnDef['colIdSort'] =>
  colDefs.filter((colDef: ColumnDef) => colDef.colId === colId)[0].field;

export const getFilterMap = (columnDefs: ColumnDef[]): FilterMap => {
  const filterMap: FilterMap = new Map() as FilterMap;
  columnDefs
    .filter((col: ColumnDef) => col.filter)
    .forEach((columnDef: ColumnDef) => {
      filterMap.set(columnDef.field, columnDef.colIdFilter);
    });
  return filterMap;
};

const constructObjectFromPath = (path: string, value: string | JsonObject): JsonObject => {
  const paths: string[] = path.split('.');
  return paths.reduceRight(
    (acc: JsonObject, item: string, index: number) => ({
      [item]: index === paths.length - 1 ? value : acc,
    }),
    {}
  );
};

const createFilterModel = (agGridFilterModel: JsonObject, filterMap: FilterMap): IFilterDef['filter'] =>
  Object.entries(agGridFilterModel).reduce((acc: IFilterDef['filter'], curr: IFilterDef['filter']) => {
    const [agGridField, filterValue]: [ColDef['field'], IFilterDef['filter']] = curr;
    let queryKey: string = filterMap.get(agGridField);
    let filterValueParsed: string | JsonObject = '';

    if (filterValue.filterType === 'text') {
      filterValueParsed = filterValue.filter;
    } else if (filterValue.filterType === 'number') {
      filterValueParsed = {
        lowerBound: filterValue.filter,
        upperBound: filterValue.filterTo ? filterValue.filterTo : filterValue.filter,
      };
    } else if (filterValue.filterType === 'date') {
      const dateFrom: string = moment.utc(filterValue.dateFrom).format();
      const dateTo: string = filterValue.dateTo ? moment.utc(filterValue.dateTo).format() : dateFrom;
      filterValueParsed = {
        lowerBound: dateFrom,
        upperBound: dateTo,
      };
      queryKey = !queryKey.endsWith('Range') ? `${queryKey}Range` : queryKey;
    } else if (filterValue.filterType === 'set') {
      filterValueParsed = [...filterValue.values];
    }

    return {
      ...acc,
      ...constructObjectFromPath(queryKey, filterValueParsed),
    };
  }, {} as IFilterDef['filter']);

export const datasource = <T extends DataModel>(
  originalConfig: AxiosRequestConfig,
  DataGridModel: Constructable<T>,
  filterMap: FilterMap,
  colDef: ColDef[]
): Datasource => ({
  async getRows(params: IServerSideGetRowsParams) {
    const filterParams: JsonObject = params.request.filterModel
      ? createFilterModel(params.request.filterModel, filterMap)
      : undefined;

    const gridQueryParams: AxiosRequestConfig['params'] = {};

    if (!isEmpty(filterParams)) {
      if (originalConfig.params?.filter) {
        gridQueryParams.filter = {
          ...originalConfig.params.filter,
          ...filterParams,
        };
      } else {
        gridQueryParams.filter = filterParams;
      }
    }

    const { sortModel } = params && params.request;
    const { colIdSort } = params.columnApi
      .getColumns()
      .find((item: Column) =>
        sortModel.length > 0 ? item.getColDef().colId === sortModel[0].colId : originalConfig.params.sort
      )
      .getColDef() as ColumnDef;

    const colSorting = colIdSort
      ? `${colIdSort},${sortModel[0].sort}`
      : sortModel.map(({ sort, colId }) => `${colId},${sort}`);

    gridQueryParams.sort = sortModel.length > 0 ? colSorting : originalConfig.params.sort;
    gridQueryParams.size = params.api.paginationGetPageSize();
    gridQueryParams.page = params.api.paginationGetCurrentPage();

    const requestConfig: AxiosRequestConfig = { ...originalConfig };

    requestConfig.params = {
      ...originalConfig.params,
      ...gridQueryParams,
    };

    let response: AxiosResponse;
    try {
      response = await axiosInstance.request<PaginatedApiResponse<T>>(requestConfig);
    } catch (error) {
      Log.error('grid helper -> fetch rowData', error);
      params.fail();
      // display no elements
      params.success({ rowData: [], rowCount: 0 });
    }
    if (response && response.data) {
      const rowData: T = new DataGridModel(response.data);
      params.success({
        rowData: rowData.dataRows || [],
        rowCount: rowData.totalRows || 0,
      });
    } else {
      // inform grid request failed
      params.fail();
      // display no elements
      params.success({ rowData: [], rowCount: 0 });
    }
  },
});

export const createDatasource = <T extends DataModel>(
  config: AxiosRequestConfig,
  DataGridModel: Constructable<T>,
  filterMap: FilterMap,
  columnMapping: ColDef[]
): Datasource =>
  useMemo<Datasource>(
    () => datasource<T>(config, DataGridModel, filterMap, columnMapping),
    [config, DataGridModel, filterMap, columnMapping]
  );
